/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axiosClient from '../../api/axiosClient';
import { notifyDashboardRefresh } from '../../utils/dashboardRefreshBus';
import PortalModal from '../../components/shared/PortalModal';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import CreateBookingModal from './CreateBookingModal';
import RescheduleModal from './RescheduleModal';
import ReassignModal from './ReassignModal';

// Icons using SVG for consistency with other admin pages
const Icons = {
  Edit: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Cancel: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Delete: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
};

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-3 md:p-4 lg:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
      <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm md:text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 hidden md:block">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'confirmed': return 'info';
    case 'completed': return 'success';
    case 'no_show': return 'error';
    case 'cancelled': return 'default';
    default: return 'default';
  }
};

// Sort helper: newest-created bookings first (fallback to id, then start time)
const bookingSortKey = (b) => {
  const created =
    b?.created_at ??
    b?.createdAt ??
    b?.created_utc ??
    b?.created ??
    b?.inserted_at;

  const createdTs = created ? Date.parse(created) : NaN;
  if (Number.isFinite(createdTs)) return createdTs;

  const id = Number(b?.id);
  if (Number.isFinite(id)) return id;

  const start = b?.start_utc ?? b?.start_local ?? b?.start_time;
  const startTs = start ? Date.parse(start) : NaN;
  return Number.isFinite(startTs) ? startTs : 0;
};

const sortBookingsNewestFirst = (items) =>
  Array.isArray(items) ? [...items].sort((a, b) => bookingSortKey(b) - bookingSortKey(a)) : [];


const BookingsNew = () => {
  const { user } = useAdminAuth();
  const { t, i18n } = useTranslation('admin');
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopTimezone, setShopTimezone] = useState('');
  
  // Get today's date range (start of day to end of day)
  const getTodayRange = () => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    return {
      from: startOfDay.toISOString().split('T')[0],
      to: endOfDay.toISOString().split('T')[0]
    };
  };
  
  const todayRange = getTodayRange();
  
  const [filters, setFilters] = useState({
    from: todayRange.from,
    to: todayRange.to,
    status: '',
    barber_id: '',
    q: '',
    page: 1,
    pageSize: 25
  });
  const [focusBookingId, setFocusBookingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0
  });
  const [barbers, setBarbers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const formatBookingStatus = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    const keyMap = {
      pending: 'admin.status_pending',
      confirmed: 'admin.status_confirmed',
      completed: 'admin.status_completed',
      no_show: 'admin.status_no_show',
      cancelled: 'admin.status_cancelled',
      canceled: 'admin.status_cancelled'
    };

    return t(keyMap[normalized] || normalized, { defaultValue: status });
  };
  
  // New state for collapsible filters and date shortcuts
  const [showFilters, setShowFilters] = useState(false);
  const [dateRangeMode, setDateRangeMode] = useState('Today'); // 'Today', '7D', '30D', 'Custom'
  const [forceDateRange, setForceDateRange] = useState(null); // { from, to } used for API only

  // Load bookings with current filters
  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const apiFrom = forceDateRange?.from ?? filters.from;
      const apiTo = forceDateRange?.to ?? filters.to;
      const params = new URLSearchParams({
        page: filters.page.toString(),
        pageSize: filters.pageSize.toString()
      });

      // Add optional filters
      if (apiFrom) params.append('from', apiFrom);
      if (apiTo) params.append('to', apiTo);
      if (filters.status) params.append('status', filters.status);
      if (filters.barber_id) params.append('barber_id', filters.barber_id);
      if (filters.q) params.append('q', filters.q);

      const response = await axiosClient.get(`/admin/bookings?${params}`);
      
      if (response.data) {
        setBookings(sortBookingsNewestFirst(response.data.items || []));
        setPagination({
          page: response.data.page,
          pageSize: response.data.pageSize,
          total: response.data.total,
          totalPages: response.data.totalPages
        });
        setShopTimezone(response.data.shop_tz);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [filters, forceDateRange]);

  // Load barbers for filter dropdown (admin only)
  const loadBarbers = useCallback(async () => {
    if (user?.role !== 'shop_admin' && user?.role !== 'superadmin') return;
    
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '100',
        status: 'active'
      });

      const response = await axiosClient.get(`/admin/barbers?${params}`);
      
      if (response.data.ok) {
        setBarbers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading barbers:', error);
    }
  }, [user?.role]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Accept deep-links from global search: ?q=...&focus_booking_id=123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const focusId = params.get('focus_booking_id');
    const from = params.get('from') || '';
    const to = params.get('to') || '';
    const pageSizeParam = params.get('pageSize') || '';

    if (q && q !== filters.q) {
      setFilters(prev => ({ ...prev, q, page: 1 }));
    }
    // Allow calendar deep-links to set a date range so the booking is guaranteed visible.
    if ((from && from !== filters.from) || (to && to !== filters.to)) {
      setFilters(prev => ({
        ...prev,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(pageSizeParam && Number.isFinite(Number(pageSizeParam)) ? { pageSize: Number(pageSizeParam) } : {}),
        page: 1,
      }));
      // Use full-day boundaries for API while keeping date-only in the UI.
      if (from || to) {
        const apiFrom = from ? `${from}T00:00:00` : '';
        const apiTo = to ? `${to}T23:59:59` : '';
        setForceDateRange({ from: apiFrom, to: apiTo });
      }
      setDateRangeMode('Custom');
      setShowFilters(false);
    } else if (!from && !to) {
      setForceDateRange(null);
    }
    if (focusId) {
      const n = Number(focusId);
      if (Number.isFinite(n)) setFocusBookingId(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // After bookings render, scroll & highlight the focused booking
  useEffect(() => {
    if (!focusBookingId) return;
    if (!bookings || bookings.length === 0) return;
    const row = document.getElementById(`booking-row-${focusBookingId}`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // Auto-clear highlight after a moment
      const t = setTimeout(() => setFocusBookingId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [focusBookingId, bookings]);

  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, page: 1 }));
  }, [filters.from, filters.to, filters.status, filters.barber_id, filters.q]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setForceDateRange(null);
    
    // If user manually changes date inputs, switch to Custom mode
    if (field === 'from' || field === 'to') {
      setDateRangeMode('Custom');
    }
  };
  
  // Handle date range shortcuts
  const handleDateRangeChange = (mode) => {
    setDateRangeMode(mode);
    setForceDateRange(null);
    
    const today = new Date();
    let from, to;
    
    switch (mode) {
      case 'Today':
        // Today: start of today → end of today
        from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        to = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        setFilters(prev => ({
          ...prev,
          from: from.toISOString().split('T')[0],
          to: to.toISOString().split('T')[0]
        }));
        break;
        
      case '7D':
        // 7D: today → 7 days ahead
        from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        to = new Date(today);
        to.setDate(to.getDate() + 7);
        setFilters(prev => ({
          ...prev,
          from: from.toISOString().split('T')[0],
          to: to.toISOString().split('T')[0]
        }));
        break;
        
      case '30D':
        // 30D: today → 30 days ahead
        from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        to = new Date(today);
        to.setDate(to.getDate() + 30);
        setFilters(prev => ({
          ...prev,
          from: from.toISOString().split('T')[0],
          to: to.toISOString().split('T')[0]
        }));
        break;
        
      case 'Custom':
        // Custom: open filters panel
        setShowFilters(true);
        break;
        
      default:
        break;
    }
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (event) => {
    setFilters(prev => ({ ...prev, pageSize: parseInt(event.target.value, 10), page: 1 }));
  };

  const handleCreateBooking = async (bookingData) => {
    try {
      const response = await axiosClient.post(`/admin/bookings`, bookingData);
      
      // Prepend new booking to the list
      setBookings(prev => sortBookingsNewestFirst([response.data, ...prev]));
      
      // Show success message
      console.log('✅ Booking created successfully!');
      
      // Trigger dashboard refresh for admin and barber dashboards
      window.dispatchEvent(new CustomEvent('bookingCreated', {
        detail: {
          booking: response.data,
          barber_id: bookingData.barber_id,
          service_id: bookingData.service_id
        }
      }));
      notifyDashboardRefresh({
        type: 'bookingCreated',
        booking_id: response.data?.id,
        barber_id: bookingData.barber_id,
        service_id: bookingData.service_id
      });
      
      // Close modal
      setShowCreateModal(false);
      
      // Reload bookings to get fresh data
      loadBookings();
      
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error; // Re-throw so modal can handle it
    }
  };

  const handleReschedule = (booking) => {
    setSelectedBooking(booking);
    setShowRescheduleModal(true);
  };

  const handleReassign = (booking) => {
    setSelectedBooking(booking);
    setShowReassignModal(true);
  };

  const handleCancel = (booking) => {
    setSelectedBooking(booking);
    setShowCancelDialog(true);
  };

  const handleRescheduleSubmit = async (newStartUtc) => {
    try {
      const response = await axiosClient.patch(`/admin/bookings/${selectedBooking.id}/reschedule`, {
        new_start_utc: newStartUtc
      });
      
      // Update booking in list
      setBookings(prev => prev.map(b => b.id === selectedBooking.id ? response.data : b));
      
      // Trigger dashboard refresh for admin and barber dashboards
      window.dispatchEvent(new CustomEvent('bookingRescheduled', {
        detail: {
          booking_id: selectedBooking.id,
          barber_id: selectedBooking.barber_user_id,
          service_id: selectedBooking.service_id,
          new_time: newStartUtc
        }
      }));
      notifyDashboardRefresh({
        type: 'bookingRescheduled',
        booking_id: selectedBooking.id,
        barber_id: selectedBooking.barber_user_id,
        service_id: selectedBooking.service_id,
        new_time: newStartUtc
      });
      
      // Close modal
      setShowRescheduleModal(false);
      setSelectedBooking(null);
      
      console.log('✅ Booking rescheduled successfully!');
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      throw error;
    }
  };

  const handleReassignSubmit = async (reassignData) => {
    try {
      const response = await axiosClient.patch(`/admin/bookings/${selectedBooking.id}/assign`, reassignData);
      
      // Update booking in list
      setBookings(prev => prev.map(b => b.id === selectedBooking.id ? response.data : b));
      
      // Trigger dashboard refresh for admin and barber dashboards
      window.dispatchEvent(new CustomEvent('bookingReassigned', {
        detail: {
          booking_id: selectedBooking.id,
          old_barber_id: selectedBooking.barber_user_id,
          new_barber_id: reassignData.barber_id,
          service_id: selectedBooking.service_id
        }
      }));
      notifyDashboardRefresh({
        type: 'bookingReassigned',
        booking_id: selectedBooking.id,
        old_barber_id: selectedBooking.barber_user_id,
        new_barber_id: reassignData.barber_id,
        service_id: selectedBooking.service_id
      });
      
      // Close modal
      setShowReassignModal(false);
      setSelectedBooking(null);
      
      console.log('✅ Booking reassigned successfully!');
    } catch (error) {
      console.error('Error reassigning booking:', error);
      throw error;
    }
  };

  const handleCancelSubmit = async (reason) => {
    try {
      const response = await axiosClient.patch(`/admin/bookings/${selectedBooking.id}/status`, {
        status: 'cancelled',
        cancel_reason: reason
      });
      
      // Update booking in list
      setBookings(prev => prev.map(b => b.id === selectedBooking.id ? response.data : b));
      
      // Trigger dashboard refresh for admin and barber dashboards
      window.dispatchEvent(new CustomEvent('bookingCancelled', {
        detail: {
          booking_id: selectedBooking.id,
          barber_id: selectedBooking.barber_user_id,
          service_id: selectedBooking.service_id,
          reason: reason
        }
      }));
      notifyDashboardRefresh({
        type: 'bookingCancelled',
        booking_id: selectedBooking.id,
        barber_id: selectedBooking.barber_user_id,
        service_id: selectedBooking.service_id,
        reason
      });
      
      // Close dialog
      setShowCancelDialog(false);
      setSelectedBooking(null);
      
      console.log('✅ Booking canceled successfully!');
    } catch (error) {
      console.error('Error canceling booking:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      // Handle specific error cases
      if (error.response?.data?.error === 'ALREADY_CANCELED') {
        // Booking is already canceled, just close the dialog
        setShowCancelDialog(false);
        setSelectedBooking(null);
        console.log('ℹ️ Booking was already canceled');
        return;
      }
      
      // For other errors, show error message
      setErrorMessage(error.response?.data?.message || 'Failed to cancel booking');
      setShowErrorModal(true);
    }
  };

  const handleRemove = async (booking) => {
    setConfirmData({
      type: 'delete',
      title: t('admin.remove_booking_title'),
      message: t('admin.remove_booking_message'),
      details: [
        `${t('admin.col_client')}: ${booking.client?.name || t('admin.unknown')}`,
        `${t('admin.col_service')}: ${booking.service?.name || t('admin.unknown')}`,
        `${t('admin.col_date_time')}: ${formatDate(booking.start_local)}`
      ],
      warning: t('admin.action_cannot_be_undone'),
      onConfirm: () => performRemove(booking)
    });
    setShowConfirmModal(true);
  };

  const performRemove = async (booking) => {

    try {
      await axiosClient.delete(`/admin/bookings/${booking.id}`);
      
      // Remove booking from the list
      setBookings(prev => prev.filter(b => b.id !== booking.id));
      
      console.log('✅ Booking removed successfully!');
    } catch (error) {
      console.error('Error removing booking:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      setErrorMessage(t('admin.error_remove_booking'));
      setShowErrorModal(true);
    }
  };


  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const locale = i18n.language || 'en';
    const weekday = date.toLocaleDateString(locale, { weekday: 'short' });
    const dateStr = date.toLocaleDateString(locale, { 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString(locale, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${weekday}, ${dateStr} ${t('admin.at_time')} ${timeStr}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString(i18n.language || 'en', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };

  const canSeeAllBookings = user?.role === 'shop_admin' || user?.role === 'superadmin';
  const canCreate = user?.role === 'shop_admin' || user?.role === 'superadmin'; // Add canCreate variable

  // Select all functionality
  const handleSelectAll = () => {
    if (selectedBookings.size === bookings.length) {
      // Deselect all
      setSelectedBookings(new Set());
    } else {
      // Select all visible bookings
      setSelectedBookings(new Set(bookings.map(booking => booking.id)));
    }
  };

  const handleSelectBooking = (bookingId) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId);
    } else {
      newSelected.add(bookingId);
    }
    setSelectedBookings(newSelected);
  };

  const isAllSelected = bookings.length > 0 && selectedBookings.size === bookings.length;
  const isPartiallySelected = selectedBookings.size > 0 && selectedBookings.size < bookings.length;

  // Bulk actions
  const handleBulkCancel = async () => {
    if (selectedBookings.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const cancelPromises = Array.from(selectedBookings).map(async (bookingId) => {
        try {
          return await axiosClient.patch(`/admin/bookings/${bookingId}/status`, {
            status: 'cancelled',
            cancel_reason: 'Bulk cancelled by admin'
          });
        } catch (error) {
          // Handle individual booking errors (like already canceled)
          if (error.response?.data?.error === 'ALREADY_CANCELED') {
            console.log(`ℹ️ Booking ${bookingId} was already canceled`);
            return { success: true, alreadyCanceled: true };
          }
          throw error;
        }
      });
      
      const results = await Promise.allSettled(cancelPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const alreadyCanceled = results.filter(r => r.status === 'fulfilled' && r.value?.alreadyCanceled).length;
      
      console.log(`✅ Successfully processed ${successful} booking(s) (${alreadyCanceled} were already canceled)`);
      setSelectedBookings(new Set());
      loadBookings();
    } catch (error) {
      console.error('Error cancelling bookings:', error);
      setErrorMessage(`Failed to cancel ${selectedBookings.size} booking(s)`);
      setShowErrorModal(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBookings.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const deletePromises = Array.from(selectedBookings).map(bookingId => 
        axiosClient.delete(`/admin/bookings/${bookingId}`)
      );
      
      await Promise.all(deletePromises);
      
      console.log(`✅ Successfully deleted ${selectedBookings.size} booking(s)`);
      setSelectedBookings(new Set());
      loadBookings();
    } catch (error) {
      console.error('Error deleting bookings:', error);
      setErrorMessage(`Failed to delete ${selectedBookings.size} booking(s)`);
      setShowErrorModal(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedBookings(new Set());
  }, [filters.from, filters.to, filters.status, filters.barber_id, filters.q, filters.page]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('admin.nav_bookings')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.bookings_subtitle')}
            </p>
            <span className="text-xs md:text-sm text-zinc-500 dark:text-zinc-400 block mt-0.5">
              {t('admin.all_times_shown_in', { timezone: shopTimezone || t('admin.local_time') })}
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="px-3 md:px-4 py-2 rounded-xl text-white text-sm md:text-base font-medium flex items-center gap-1.5 md:gap-2 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
              style={{ background: ACCENT }}
            >
              <AddIcon className="h-4 w-4" />
              <span className="hidden xs:inline">{t('admin.create_booking')}</span>
              <span className="xs:hidden">{t('admin.create')}</span>
            </motion.button>
          </div>
        </div>
      </div>

        {/* Date Range Shortcuts */}
        <div className="mb-4 md:mb-6">
          <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
            {/* Date shortcuts */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateRangeChange('Today')}
              className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                dateRangeMode === 'Today'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('admin.today')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateRangeChange('7D')}
              className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                dateRangeMode === '7D'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              7D
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateRangeChange('30D')}
              className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                dateRangeMode === '30D'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              30D
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateRangeChange('Custom')}
              className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                dateRangeMode === 'Custom'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('admin.range_custom')}
            </motion.button>
            
            {/* Filters Toggle */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-all ml-auto"
            >
              <FilterIcon className="h-4 w-4" />
              <span>{t('admin.filters')}</span>
              <motion.svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ rotate: showFilters ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </motion.button>
          </div>
          
          {/* Collapsible Filters Panel */}
          <motion.div
            initial={false}
            animate={{
              height: showFilters ? 'auto' : 0,
              opacity: showFilters ? 1 : 0
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <Card>
              <CardHeader
                title={t('admin.filters')}
                subtitle={t('admin.bookings_filters_subtitle')}
                icon={
                  <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                  </svg>
                }
              />
              <div className="p-3 md:p-4 lg:p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                      {t('admin.from_date')}
                    </label>
                    <input
                      type="date"
                      value={filters.from}
                      onChange={(e) => handleFilterChange('from', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                      style={{ '--accent': ACCENT }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                      {t('admin.to_date')}
                    </label>
                    <input
                      type="date"
                      value={filters.to}
                      onChange={(e) => handleFilterChange('to', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                      style={{ '--accent': ACCENT }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                      {t('admin.col_status')}
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                      style={{ '--accent': ACCENT }}
                    >
                      <option value="">{t('admin.all_statuses')}</option>
                      <option value="pending">{t('admin.status_pending')}</option>
                      <option value="confirmed">{t('admin.status_confirmed')}</option>
                      <option value="completed">{t('admin.status_completed')}</option>
                      <option value="no_show">{t('admin.status_no_show')}</option>
                      <option value="cancelled">{t('admin.status_cancelled')}</option>
                    </select>
                  </div>
                  {canSeeAllBookings && (
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                        {t('admin.col_barber')}
                      </label>
                      <select
                        value={filters.barber_id}
                        onChange={(e) => handleFilterChange('barber_id', e.target.value)}
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                        style={{ '--accent': ACCENT }}
                      >
                        <option value="">{t('admin.all_barbers')}</option>
                        {barbers.map(barber => (
                          <option key={barber.id} value={barber.id}>
                            {barber.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={canSeeAllBookings ? "sm:col-span-2" : "sm:col-span-3"}>
                    <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                      {t('admin.search_by_client')}
                    </label>
                    <div className="relative">
                      <SearchIcon className="absolute left-2 md:left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-zinc-400" />
                      <input
                        type="text"
                        value={filters.q}
                        onChange={(e) => handleFilterChange('q', e.target.value)}
                        placeholder={t('admin.search_name_email_phone')}
                        className="w-full pl-8 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                        style={{ '--accent': ACCENT }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Bulk Actions */}
        {selectedBookings.size > 0 && (
          <Card className="mb-6">
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    {t(selectedBookings.size === 1 ? 'admin.bookings_selected_one' : 'admin.bookings_selected_other', { count: selectedBookings.size })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkCancel}
                    disabled={bulkActionLoading}
                    className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bulkActionLoading ? t('admin.cancelling') : t('admin.cancel_selected')}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bulkActionLoading ? t('admin.deleting') : t('admin.delete_selected')}
                  </motion.button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Bookings Table */}
        <Card>
          <CardHeader
            title={t('admin.nav_bookings')}
            subtitle={t('admin.total_bookings_count', { count: pagination.total })}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }
          />
          <div className="admin-table-container admin-table-container--mobile-scroll">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                  <th className="py-4 px-4 font-medium hidden md:table-cell">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartiallySelected;
                      }}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-[--accent] focus:ring-[--accent]"
                      style={{ '--accent': ACCENT }}
                    />
                  </th>
                  <th className="py-4 px-4 font-medium">{t('admin.col_client')}</th>
                  {/* Mobile-only columns (use horizontal swipe-scroll) */}
                  <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_barber')}</th>
                  <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_service')}</th>
                  <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_date_time')}</th>
                  <th className="py-4 px-4 font-medium md:hidden">{t('admin.end_time')}</th>
                  <th className="py-4 px-4 font-medium hidden md:table-cell">{t('admin.col_barber')}</th>
                  <th className="py-4 px-4 font-medium hidden lg:table-cell">{t('admin.col_service')}</th>
                  <th className="py-4 px-4 font-medium hidden lg:table-cell">{t('admin.col_date_time')}</th>
                  <th className="py-4 px-4 font-medium hidden xl:table-cell">{t('admin.end_time')}</th>
                  <th className="py-4 px-4 font-medium">{t('admin.col_status')}</th>
                  <th className="py-4 px-4 font-medium text-right">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 dark:divide-white/10">
                {loading ? (
                  <>
                    {/* Mobile */}
                    <tr className="md:hidden">
                      <td colSpan={7} className="py-12 text-center">
                        <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                          <CircularProgress size={24} style={{ color: ACCENT }} />
                          <span>{t('admin.loading_bookings')}</span>
                        </div>
                      </td>
                    </tr>
                    {/* Tablet/Desktop */}
                    <tr className="hidden md:table-row">
                      <td colSpan={8} className="py-12 text-center">
                        <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                          <CircularProgress size={24} style={{ color: ACCENT }} />
                          <span>{t('admin.loading_bookings')}</span>
                        </div>
                      </td>
                    </tr>
                  </>
                ) : bookings.length === 0 ? (
                  <>
                    {/* Mobile */}
                    <tr className="md:hidden">
                      <td colSpan={7} className="py-12 text-center">
                        <div className="text-center">
                          <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                            {t('admin.no_bookings')}
                          </h3>
                          <p className="text-zinc-500 dark:text-zinc-400">
                            {canCreate ? t('admin.no_bookings_create_first') : t('admin.no_bookings_match_filters')}
                          </p>
                        </div>
                      </td>
                    </tr>
                    {/* Tablet/Desktop */}
                    <tr className="hidden md:table-row">
                      <td colSpan={8} className="py-12 text-center">
                        <div className="text-center">
                          <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                            {t('admin.no_bookings')}
                          </h3>
                          <p className="text-zinc-500 dark:text-zinc-400">
                            {canCreate ? t('admin.no_bookings_create_first') : t('admin.no_bookings_match_filters')}
                          </p>
                        </div>
                      </td>
                    </tr>
                  </>
                ) : (
                  bookings.map((booking) => (
                    <motion.tr 
                      key={booking.id} 
                      id={`booking-row-${booking.id}`}
                      className={`text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors ${
                        focusBookingId === booking.id ? 'ring-2 ring-[#7d6633] ring-offset-2 ring-offset-white dark:ring-offset-zinc-900' : ''
                      }`}
                      whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <td className="py-4 px-4 hidden md:table-cell">
                        <input
                          type="checkbox"
                          checked={selectedBookings.has(booking.id)}
                          onChange={() => handleSelectBooking(booking.id)}
                          className="rounded border-gray-300 text-[--accent] focus:ring-[--accent]"
                          style={{ '--accent': ACCENT }}
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">
                            {booking.client?.name || t('admin.unknown')}
                          </div>
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            {booking.client?.email}
                          </div>
                          {/* Tablet: Show Service + Date/Time stacked (keeps tablet UX unchanged) */}
                          <div className="hidden md:block lg:hidden mt-2 space-y-1">
                            <div className="text-xs text-zinc-600 dark:text-zinc-300 font-medium">
                              {booking.service?.name || t('admin.unknown')}
                            </div>
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                              {formatTime(booking.start_local)} – {new Date(booking.end_local).toLocaleTimeString(i18n.language || 'en', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Mobile-only columns (use horizontal swipe-scroll) */}
                      <td className="py-4 px-4 md:hidden">
                        <div>
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">
                            {booking.barber?.name || t('admin.unknown')}
                          </div>
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            {booking.barber?.email}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 md:hidden">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {booking.service?.name || t('admin.unknown')}
                        </div>
                      </td>
                      <td className="py-4 px-4 md:hidden">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {formatTime(booking.start_local)}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                          {shopTimezone || t('admin.local_time')}
                        </div>
                      </td>
                      <td className="py-4 px-4 md:hidden">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {formatTime(booking.end_local)}
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell">
                        <div>
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">
                            {booking.barber?.name || t('admin.unknown')}
                          </div>
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            {booking.barber?.email}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden lg:table-cell">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {booking.service?.name || t('admin.unknown')}
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden lg:table-cell">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {formatTime(booking.start_local)}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                          {shopTimezone || t('admin.local_time')}
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden xl:table-cell">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {formatTime(booking.end_local)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                          booking.status === 'confirmed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                          booking.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                          booking.status === 'no_show' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                          booking.status === 'cancelled' ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' :
                          'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
                        }`}>
                          {formatBookingStatus(booking.status)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center gap-1 md:gap-2 justify-end">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleReschedule(booking)}
                            className="p-1.5 md:p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={t('admin.reschedule_booking')}
                          >
                            <Icons.Edit />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleCancel(booking)}
                            className="p-1.5 md:p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title={t('admin.cancel_booking')}
                          >
                            <Icons.Cancel />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRemove(booking)}
                            className="p-1.5 md:p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title={t('admin.delete_permanently')}
                          >
                            <Icons.Delete />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination */}
        <Card className="mt-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between admin-pagination-wrapper">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 admin-pagination-info">
                {t('admin.showing_results_range', { from: ((filters.page - 1) * filters.pageSize) + 1, to: Math.min(filters.page * filters.pageSize, pagination.total), total: pagination.total, entity: t('admin.nav_bookings').toLowerCase() })}
              </div>
              <div className="flex items-center gap-4 admin-pagination-controls">
                <div className="flex items-center gap-2 admin-pagination-size-selector">
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">{t('admin.rows_per_page')}</span>
                  <select
                    value={filters.pageSize}
                    onChange={handlePageSizeChange}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 admin-pagination-buttons">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page <= 1}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.previous')}
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {t('admin.page_of', { page: filters.page, total: pagination.totalPages })}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page >= pagination.totalPages}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.next')}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </Card>


        {/* Create Booking Modal */}
        <CreateBookingModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateBooking}
          userRole={user?.role}
          userId={user?.id}
        />

        {/* Reschedule Modal */}
        <RescheduleModal
          open={showRescheduleModal}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedBooking(null);
          }}
          onSubmit={handleRescheduleSubmit}
          booking={selectedBooking}
          isDark={isDark}
          user={user}
        />

        {/* Reassign Modal */}
        <ReassignModal
          open={showReassignModal}
          onClose={() => {
            setShowReassignModal(false);
            setSelectedBooking(null);
          }}
          onSubmit={handleReassignSubmit}
          booking={selectedBooking}
          userRole={user?.role}
          userId={user?.id}
          isDark={isDark}
        />

        {/* Cancel Dialog - Functional */}
        {showCancelDialog && selectedBooking && (
          <PortalModal open={showCancelDialog}>
            <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
              >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                  {t('admin.cancel_booking')}
                </h3>
              </div>
              <p className="text-zinc-600 dark:text-zinc-400 mb-4">
                {t('admin.cancel_booking_for_client', { client: selectedBooking.client?.name || t('admin.unknown_client') })}
              </p>
              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.cancellation_reason_required')}
                </label>
                <textarea
                  id="cancelReason"
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/30 transition-all resize-none"
                  placeholder={t('admin.cancellation_reason_placeholder')}
                />
              </div>
              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowCancelDialog(false);
                    setSelectedBooking(null);
                  }}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  {t('admin.cancel')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const reason = document.getElementById('cancelReason').value.trim();
                    if (reason) {
                      handleCancelSubmit(reason);
                    } else {
                      setErrorMessage(t('admin.error_cancellation_reason_required'));
                      setShowErrorModal(true);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
                >
                  {t('admin.confirm_cancel')}
                </motion.button>
              </div>
              </motion.div>
            </div>
          </PortalModal>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && confirmData && (
          <PortalModal open={showConfirmModal}>
            <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="hc-modal-panel p-4 md:p-6 rounded-2xl shadow-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
              >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  {confirmData.title}
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                  {confirmData.message}
                </p>
                {confirmData.details && (
                  <div className="bg-zinc-100 dark:bg-zinc-800/50 p-4 rounded-xl text-sm border border-white/10 dark:border-white/10">
                    {confirmData.details.map((detail, index) => (
                      <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                    ))}
                  </div>
                )}
                {confirmData.warning && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                    {confirmData.warning}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  {t('admin.cancel')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowConfirmModal(false);
                    confirmData.onConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
                >
                  {t('admin.confirm')}
                </motion.button>
              </div>
              </motion.div>
            </div>
          </PortalModal>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <PortalModal open={showErrorModal}>
            <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="hc-modal-panel p-4 md:p-6 rounded-2xl shadow-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
              >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  {t('admin.error')}
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {errorMessage}
                </p>
              </div>
              
              <div className="flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowErrorModal(false)}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  {t('admin.ok')}
                </motion.button>
              </div>
              </motion.div>
            </div>
          </PortalModal>
        )}
    </div>
  );
};

export default BookingsNew;
